import React, { useState, useEffect } from 'react';

const BACKEND = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);

/**
 * FullSheetBuilder — choose which sheets go on the FULL SHEET, then print them
 * as one document. Rooms and categories scope every chosen sheet at once, so
 * "just the Kitchen, just the tile" is one click rather than eight prints.
 *
 * The composition is a print-time choice, not saved state: what each sheet
 * *contains* still lives on that sheet (Save there), so nothing here can
 * silently change a trade's sheet.
 */
export default function FullSheetBuilder({ projectId, onClose }) {
  const [opts, setOpts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sheets, setSheets] = useState({});        // { key: bool }
  const [rooms, setRooms] = useState({});          // { room_id: bool }
  const [cats, setCats] = useState({});            // { name: bool }
  const [withRoomSheet, setWithRoomSheet] = useState(true);
  const [contents, setContents] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND}/api/export-full-sheet/${projectId}/options`);
        if (!res.ok) throw new Error(`Could not load the sheet list (${res.status})`);
        const data = await res.json();
        if (!alive) return;
        setOpts(data);
        // Everything on by default — the client's "show all" starting point.
        setSheets(Object.fromEntries((data.sheets || []).map(s => [s.key, true])));
        setRooms(Object.fromEntries((data.rooms || []).map(r => [r.room_id, true])));
        setCats(Object.fromEntries((data.categories || []).map(c => [c, true])));
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [projectId]);

  const flip = (setter) => (key) => setter(prev => ({ ...prev, [key]: !prev[key] }));
  const setAll = (setter, keys, on) => setter(Object.fromEntries(keys.map(k => [k, on])));

  const chosen = (map) => Object.keys(map).filter(k => map[k]);
  const chosenSheets = chosen(sheets);
  const chosenRooms = chosen(rooms);
  const chosenCats = chosen(cats);

  const allRoomsOn = opts && chosenRooms.length === (opts.rooms || []).length;
  const allCatsOn = opts && chosenCats.length === (opts.categories || []).length;

  const openFullSheet = () => {
    const qs = new URLSearchParams();
    qs.set('sheets', chosenSheets.join(','));
    // Omit a scope entirely when everything is ticked, so the URL stays honest
    // about meaning "no filter" rather than "these happen to be all of them".
    if (!allRoomsOn) qs.set('rooms', chosenRooms.join(','));
    if (!allCatsOn) qs.set('categories', chosenCats.join(','));
    if (withRoomSheet) qs.set('room_sheet', '1');
    if (!contents) qs.set('contents', 'false');
    window.open(`${BACKEND}/api/export-full-sheet/${projectId}/print?${qs.toString()}`, '_blank');
  };

  const box = 'w-4 h-4 accent-[#D4A574]';
  const pill = 'px-2 py-0.5 rounded text-[11px] bg-slate-700 hover:bg-slate-600 text-gray-200';

  return (
    <div className="fixed inset-0 bg-black/90 z-50 overflow-y-auto p-4">
      <div className="max-w-3xl mx-auto bg-[#0F172A] border border-[#D4A574]/40 rounded-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#D4A574]/30">
          <div>
            <h2 className="text-xl font-bold text-[#D4A574]">📚 FULL SHEET</h2>
            <p className="text-[11px] text-[#B49B7E]">Pick the sheets to bind together. Each one keeps its own branded layout and starts a new page.</p>
          </div>
          <button onClick={onClose} className="px-3 py-2 text-gray-300 hover:text-white text-sm">✕ Close</button>
        </div>

        <div className="p-6 space-y-6">
          {loading && <p className="text-center text-[#D4C5A9] py-8">Loading…</p>}
          {error && <p className="text-center text-red-400 py-8">{error}</p>}

          {opts && (
            <>
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-bold text-[#D4C5A9] uppercase tracking-wide">Sheets on the full sheet</h3>
                  <div className="flex gap-2">
                    <button className={pill} data-testid="fs-sheets-all"
                      onClick={() => setAll(setSheets, opts.sheets.map(s => s.key), true)}>Select all</button>
                    <button className={pill} data-testid="fs-sheets-none"
                      onClick={() => setAll(setSheets, opts.sheets.map(s => s.key), false)}>None</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {opts.sheets.map(s => (
                    <label key={s.key} className="flex items-center gap-2 text-sm text-white cursor-pointer">
                      <input type="checkbox" className={box} checked={!!sheets[s.key]}
                        onChange={() => flip(setSheets)(s.key)} data-testid={`fs-sheet-${s.key}`} />
                      {s.label}
                    </label>
                  ))}
                </div>
              </section>

              <section>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-bold text-[#D4C5A9] uppercase tracking-wide">Rooms</h3>
                  <div className="flex gap-2">
                    <button className={pill} data-testid="fs-rooms-all"
                      onClick={() => setAll(setRooms, opts.rooms.map(r => r.room_id), true)}>Select all</button>
                    <button className={pill} data-testid="fs-rooms-none"
                      onClick={() => setAll(setRooms, opts.rooms.map(r => r.room_id), false)}>None</button>
                  </div>
                </div>
                {opts.rooms.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">This project has no rooms yet.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {opts.rooms.map(r => (
                      <label key={r.room_id} className="flex items-center gap-2 text-sm text-white cursor-pointer">
                        <input type="checkbox" className={box} checked={!!rooms[r.room_id]}
                          onChange={() => flip(setRooms)(r.room_id)} data-testid={`fs-room-${r.room_id}`} />
                        <span className="truncate" title={`${r.room_name} — ${r.floor}`}>{r.room_name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </section>

              <section>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-bold text-[#D4C5A9] uppercase tracking-wide">Categories</h3>
                  <div className="flex gap-2">
                    <button className={pill} data-testid="fs-cats-all"
                      onClick={() => setAll(setCats, opts.categories, true)}>Show all</button>
                    <button className={pill} data-testid="fs-cats-none"
                      onClick={() => setAll(setCats, opts.categories, false)}>None</button>
                  </div>
                </div>
                {opts.categories.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No categories in the FF&amp;E yet.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {opts.categories.map(c => (
                      <label key={c} className="flex items-center gap-2 text-sm text-white cursor-pointer">
                        <input type="checkbox" className={box} checked={!!cats[c]}
                          onChange={() => flip(setCats)(c)} data-testid={`fs-cat-${c}`} />
                        <span className="truncate">{c}</span>
                      </label>
                    ))}
                  </div>
                )}
              </section>

              <section className="space-y-2 border-t border-[#D4A574]/20 pt-4">
                <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                  <input type="checkbox" className={box} checked={withRoomSheet}
                    onChange={e => setWithRoomSheet(e.target.checked)} data-testid="fs-room-sheet" />
                  Show the room sheet on every export sheet (plan, dimensions and finishes)
                </label>
                <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                  <input type="checkbox" className={box} checked={contents}
                    onChange={e => setContents(e.target.checked)} data-testid="fs-contents" />
                  Start with a contents page
                </label>
              </section>

              <div className="flex items-center justify-between gap-3 border-t border-[#D4A574]/20 pt-4">
                <p className="text-xs text-[#B49B7E]">
                  {chosenSheets.length} sheet(s)
                  {allRoomsOn ? ', all rooms' : `, ${chosenRooms.length} room(s)`}
                  {allCatsOn ? ', all categories' : `, ${chosenCats.length} category(ies)`}
                </p>
                <button onClick={openFullSheet} disabled={!chosenSheets.length || !chosenRooms.length || !chosenCats.length}
                  data-testid="fs-open"
                  className="px-5 py-2.5 bg-[#D4A574] hover:bg-[#E2B989] disabled:opacity-40 text-black rounded font-bold">
                  🖨️ Open FULL SHEET
                </button>
              </div>
              {(!chosenSheets.length || !chosenRooms.length || !chosenCats.length) && (
                <p className="text-[11px] text-amber-400 text-right">
                  Pick at least one sheet, one room and one category.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
